// Default timeout for any backend fetch. The dashboard polls aggressively,
// so a slow or unreachable edge must fail fast — otherwise concurrent fetches
// stack up and the UI shows "loading…" until the browser TCP timeout (~30s).
export const DEFAULT_FETCH_TIMEOUT_MS = 5000;

// fetchWithTimeout wraps fetch with an AbortSignal that fires after
// timeoutMs. Callers can also pass their own signal via opts.signal; both
// signals are honoured (whichever aborts first wins).
//
// On timeout the returned promise rejects with a TimeoutError (browser
// AbortSignal.timeout), which has the same shape as user-aborted fetches —
// so callers can `if (err.name === "AbortError" || err.name === "TimeoutError") return`
// to silently swallow expected aborts.
export function fetchWithTimeout(url, opts = {}, timeoutMs = DEFAULT_FETCH_TIMEOUT_MS) {
  const { signal: callerSignal, ...rest } = opts;
  const timeoutSignal = AbortSignal.timeout(timeoutMs);
  let signal = timeoutSignal;
  if (callerSignal) {
    if (typeof AbortSignal.any === "function") {
      signal = AbortSignal.any([timeoutSignal, callerSignal]);
    } else {
      const ctrl = new AbortController();
      const onAbort = () => ctrl.abort(callerSignal.reason);
      const onTimeout = () => ctrl.abort(timeoutSignal.reason);
      callerSignal.addEventListener("abort", onAbort, { once: true });
      timeoutSignal.addEventListener("abort", onTimeout, { once: true });
      signal = ctrl.signal;
    }
  }
  return fetch(url, { ...rest, signal });
}

// isAbortError returns true for both user-aborts (AbortController) and
// timeouts (AbortSignal.timeout). Useful in catch blocks where you want to
// silently ignore expected cancellations.
export function isAbortError(err) {
  return (
    err &&
    (err.name === "AbortError" || err.name === "TimeoutError")
  );
}
